import React, { useMemo, useState } from "react";
import {
    PieChart,
    Pie,
    Cell,
    Tooltip,
    Legend,
    ResponsiveContainer,
    Sector,
} from "recharts";

// 🎨 COLORES ESTANDARIZADOS
const COLOR_BY_NAME = {
    Móvil: "#10b981",   // Verde
    Fijo: "#3b82f6",    // Azul
    Entrantes: "#ef4444", // Rojo
    Buzón: "#1e293b",   // Negro
};

const fmt = new Intl.NumberFormat("es-MX");

const ActiveShape = (props) => {
    const { cx, cy, innerRadius, outerRadius, startAngle, endAngle, fill } = props;
    return (
        <g>
            <Sector
                cx={cx}
                cy={cy}
                innerRadius={innerRadius}
                outerRadius={outerRadius + 8}
                startAngle={startAngle}
                endAngle={endAngle}
                fill={fill}
                stroke="#fff"
                strokeWidth={2}
            />
        </g>
    );
};

function PercentLabel({ cx, cy, midAngle, outerRadius, value, name, total }) {
    if (!total || !value) return null;
    const pct = ((Number(value) / total) * 100).toFixed(1);
    if (Number(pct) < 1) return null;

    const RAD = Math.PI / 180;
    const r = outerRadius + 20;
    const x = cx + r * Math.cos(-midAngle * RAD);
    const y = cy + r * Math.sin(-midAngle * RAD);

    return (
        <g>
            <line
                x1={cx + (outerRadius + 2) * Math.cos(-midAngle * RAD)}
                y1={cy + (outerRadius + 2) * Math.sin(-midAngle * RAD)}
                x2={x - (x >= cx ? -4 : 4)}
                y2={y}
                stroke="#cbd5e1"
                strokeWidth={1}
            />
            <text
                x={x}
                y={y}
                textAnchor={x >= cx ? "start" : "end"}
                dominantBaseline="middle"
                className="fill-gray-700"
                style={{ fontSize: 11, fontWeight: 600 }}
            >
                {name}: {pct}%
            </text>
        </g>
    );
}

function Center({ total, active }) {
    const rawValue = Number(active?.value ?? total ?? 0) || 0;
    const valueStr = fmt.format(rawValue);
    const len = valueStr.length;

    let fontSize = 18;
    if (len >= 9) fontSize = 16;
    if (len >= 11) fontSize = 14;
    if (len >= 13) fontSize = 12;

    const pct = total
        ? (((active?.value || 0) / total) * 100).toFixed(1)
        : "0.0";

    return (
        <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
            <div className="text-center leading-tight">
                <div
                    className="font-bold text-gray-900"
                    style={{
                        fontSize,
                        lineHeight: 1.1,
                        wordBreak: "break-word",
                        maxWidth: "7rem",
                        margin: "0 auto",
                    }}
                >
                    {valueStr}
                </div>
                <div className="text-[10px] text-gray-500 mt-0.5">
                    {active ? active.name : "Llamadas"}
                </div>
                {active && (
                    <div
                        className="text-[11px] font-semibold mt-0.5"
                        style={{ color: COLOR_BY_NAME[active.name] }}
                    >
                        {pct}%
                    </div>
                )}
            </div>
        </div>
    );
}

const CustomTooltip = ({ active, payload }) => {
    if (!active || !payload || !payload.length) return null;
    const data = payload[0];

    return (
        <div className="bg-white p-2 shadow-lg rounded-lg border border-gray-200">
            <div className="flex items-center gap-1.5 mb-1">
                <div
                    className="w-2.5 h-2.5 rounded-full"
                    style={{ backgroundColor: data.payload.fill }}
                />
                <span className="text-xs font-semibold text-gray-900">
                    {data.name}
                </span>
            </div>
            <div className="text-[11px] text-gray-600">
                Llamadas:{" "}
                <span className="font-semibold">{fmt.format(data.value)}</span>
            </div>
        </div>
    );
};

export default function GraficaLlamadasPie({ resumen }) {
    const includeBuzon = true;

    const data = useMemo(() => {
        const base = [
            { name: "Móvil", value: Number(resumen?.llamadas_movil ?? 0) },
            { name: "Fijo", value: Number(resumen?.llamadas_fijo ?? 0) },
            { name: "Entrantes", value: Number(resumen?.llamadas_entrada ?? 0) },
        ];
        const withBuzon = includeBuzon
            ? [...base, { name: "Buzón", value: Number(resumen?.buzon ?? 0) }]
            : base;
        return withBuzon.filter((d) => Number(d.value) > 0);
    }, [resumen, includeBuzon]);

    const total = data.reduce((a, d) => a + (Number(d.value) || 0), 0);
    const [activeIndex, setActiveIndex] = useState(-1);
    const activeItem = activeIndex >= 0 ? data[activeIndex] : null;

    if (data.length === 0 || total === 0) {
        return (
            <div className="relative h-[280px] w-full bg-white shadow rounded-xl p-3 flex items-center justify-center">
                <div className="text-center">
                    <svg
                        className="w-12 h-12 mx-auto text-gray-300 mb-2"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                    >
                        <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"
                        />
                    </svg>
                    <p className="text-xs font-semibold text-gray-600">
                        Sin datos de llamadas
                    </p>
                    <p className="text-[11px] text-gray-500 mt-1">
                        Selecciona un rango con datos
                    </p>
                </div>
            </div>
        );
    }

    return (
        <div className="relative h-[280px] w-full bg-white shadow rounded-xl p-3">
            <div className="text-center mb-2">
                <p className="text-xs font-semibold text-gray-800">Total de llamadas</p>
                <p className="text-[10px] text-gray-500">
                    Móvil, Fijo, Entrantes{includeBuzon ? " y Buzón" : ""}
                </p>
            </div>

            <Center total={total} active={activeItem} />

            <ResponsiveContainer width="100%" height="87%">
                <PieChart>
                    <defs>
                        {data.map((d, i) => {
                            const color = COLOR_BY_NAME[d.name];
                            return (
                                <linearGradient
                                    key={i}
                                    id={`gradient-llamadas-${d.name}`}
                                    x1="0"
                                    y1="0"
                                    x2="0"
                                    y2="1"
                                >
                                    <stop offset="0%" stopColor={color} stopOpacity={1} />
                                    <stop offset="100%" stopColor={color} stopOpacity={0.8} />
                                </linearGradient>
                            );
                        })}
                    </defs>
                    <Pie
                        data={data}
                        dataKey="value"
                        nameKey="name"
                        innerRadius={50}
                        outerRadius={72}
                        paddingAngle={2}
                        minAngle={5}
                        labelLine={false}
                        label={(p) => <PercentLabel {...p} total={total} />}
                        isAnimationActive={true}
                        animationDuration={800}
                        animationBegin={0}
                        activeIndex={activeIndex}
                        activeShape={ActiveShape}
                        onClick={(_, i) => setActiveIndex(i)}
                        onMouseEnter={(_, i) => setActiveIndex(i)}
                        onMouseLeave={() => setActiveIndex(-1)}
                    >
                        {data.map((d, i) => (
                            <Cell
                                key={i}
                                fill={`url(#gradient-llamadas-${d.name})`}
                                stroke="#fff"
                                strokeWidth={1.5}
                            />
                        ))}
                    </Pie>

                    <Tooltip content={<CustomTooltip />} />
                    <Legend
                        wrapperStyle={{ fontSize: "11px", paddingTop: "6px" }}
                        iconType="circle"
                        iconSize={8}
                    />
                </PieChart>
            </ResponsiveContainer>
        </div>
    );
}